<template>
  <div>
    <!-- RTT Not Enabled Warning -->
    <b-alert
      v-if="hasCheckedRTTEnabled && !isRTTEnabled"
      show
      variant="danger"
      class="mb-4"
    >
      <strong>RTT Creation Request Unavailable</strong>
      <p class="mb-2 mt-1">
        You need to have the RTT feature enabled or switch to a billing plan that supports Ring Tree Targets in your Ringba account.
      </p>
      <b-button
        variant="outline-danger"
        size="sm"
        :to="{ name: 'SellerAvailableOffers' }"
      >
        Return to Offers
      </b-button>
    </b-alert>

    <validation-observer
      ref="formValidation"
      v-slot="{ handleSubmit }"
    >
      <b-form
        novalidate
        @submit.stop.prevent="handleSubmit(onSubmit)"
      >
        <!-- Offer Name - Display Only -->
        <BaseFormInput
          id="offerName"
          :value="offerName"
          label="Offer Name"
          type="text"
          :horizontal="true"
          internal-platform-input
          :small="true"
          width="5"
          disabled
          readonly
        />

        <!-- Category - Display Only -->
        <BaseFormInput
          id="category"
          :value="categoryName"
          label="Category"
          type="text"
          :horizontal="true"
          internal-platform-input
          :small="true"
          width="5"
          disabled
          readonly
        />

        <!-- Traffic Source - Dropdown -->
        <BaseFormSelect
          v-if="!showCreativeId"
          id="trafficSource"
          v-model="rttRequestFormData.trafficSource"
          :options="trafficSourceOptions"
          label="Traffic source"
          name="Traffic source"
          :horizontal="true"
          internal-platform-input
          :small="true"
          width="5"
          required
        />

        <!-- Third Party - Radio Buttons -->
        <BaseFormRadio
          v-model="rttRequestFormData.thirdParty"
          :options="thirdPartyOptions"
          name="Third Party"
          label="Third Party"
          :horizontal="true"
          internal-platform-input
          width="5"
          required
        />

        <!-- Creative Selection - Dropdown (Conditional) -->
        <BaseFormSelect
          v-if="showCreativeId"
          id="creativeId"
          v-model="rttRequestFormData.creativeId"
          :options="creativeOptions"
          label="Creative"
          name="Creative"
          :horizontal="true"
          internal-platform-input
          :small="true"
          width="5"
          :rules="creativeIdRules"
          :disabled="loadingCreatives"
        />

        <!-- Create Ring Tree Checkbox -->
        <b-form-group
          content-cols="12"
          content-cols-sm="5"
          content-cols-lg="4"
          content-cols-xl="4"
          label-cols-sm="3"
          label-cols="12"
        >
          <BaseFormCheckbox
            id="createRingTree"
            v-model="rttRequestFormData.createRingTree"
            label="Create Ring Tree"
            name="Create Ring Tree"
            :horizontal="true"
            internal-platform-input
            width="5"
          >
            Create a Ring Tree
          </BaseFormCheckbox>
        </b-form-group>

        <!-- Info Text -->
        <b-form-group
          content-cols="12"
          content-cols-sm="6"
          content-cols-lg="5"
          content-cols-xl="5"
          label-cols-sm="3"
          label-cols="12"
        >
          <b-alert show variant="info" class="mb-0">
            To include optional parameters in your RTT, add them to the URL in Request Settings using '&amp;' (e.g., &amp;age=65&amp;addressState=CA). A link to edit the RTT in Ringba will be available once the request is completed.
          </b-alert>
        </b-form-group>

        <!-- Submit Button -->
        <BaseButtonLoader
          id="submit-button"
          :horizontal="true"
          internal-platform-input
          variant="primary"
          :loading="isSubmitting"
          :disabled="isSubmitDisabled"
          type="submit"
          class="mt-4"
        >
          Submit Request
        </BaseButtonLoader>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'

export default {
  name: 'SellerOfferCreateRTTRequestForm',

  props: {
    sellerOfferRegistration: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      rttRequestFormData: {
        trafficSource: '',
        thirdParty: 'false',
        creativeId: '',
        createRingTree: false
      },
      isSubmitting: false,
      loadingCreatives: false,
      isCheckingRTTEnabled: false,
    }
  },

  computed: {
    ...mapGetters('auth', ['hasAllowedMedicareCategory']),
    ...mapGetters('sellerOffer', ['canCreateRingTreeTarget', 'hasCheckedCanCreateRTT']),
    ...mapState('creative', ['creatives']),

    isRTTEnabled() {
      return this.canCreateRingTreeTarget
    },

    hasCheckedRTTEnabled() {
      return this.hasCheckedCanCreateRTT
    },

    isSubmitDisabled() {
      return !this.isRTTEnabled || this.isCheckingRTTEnabled
    },

    showCreativeId() {
      return this.hasAllowedMedicareCategory && this.sellerOfferRegistration?.sellerOffer?.category?.isMedicare
    },

    offerName() {
      return this.sellerOfferRegistration?.sellerOffer?.name || ''
    },

    categoryName() {
      return this.sellerOfferRegistration?.category?.name || ''
    },

    trafficSourceOptions() {
      const sources = this.sellerOfferRegistration?.approvedTrafficSources || []
      
      const options = sources.map(source => ({
        value: source.subId,
        text: `${source.name} (${source.trafficSourceType})`
      }))

      // Add placeholder option
      options.unshift({
        value: '',
        text: 'Select traffic source'
      })

      return options
    },

    thirdPartyOptions() {
      const isAffiliateTrafficApproved = this.sellerOfferRegistration?.isAffiliateTrafficApproved
      
      return [
        { value: true, text: 'Yes', disabled: !isAffiliateTrafficApproved },
        { value: false, text: 'No' }
      ]
    },

    isMedicare() {
      return this.sellerOfferRegistration?.sellerOffer?.category?.isMedicare
    },

    creativeIdRules() {
      return this.isMedicare ? 'required' : ''
    },

    creativeOptions() {
      if (!this.creatives || this.creatives.length === 0) {
        return [{
          value: '',
          text: 'No creatives available'
        }]
      }
      
      // Filter creatives based on category and status
      const filteredCreatives = this.creatives.filter(creative => {
        // Only show approved and enabled creatives
        const isAvailable = creative.status === 'Approved' && creative.enabled
        
        // For Medicare offers, show only Medicare creatives
        // For non-Medicare offers, show only non-Medicare creatives
        const categoryMatch = this.isMedicare ? creative.isMedicare : !creative.isMedicare
        
        return isAvailable && categoryMatch
      })

      const options = filteredCreatives.map(creative => ({
        value: creative.id,
        text: `${creative.name} (Id: ${creative.id}, SMID: ${creative.cmsId})`
      }))

      // Add placeholder option at the beginning
      options.unshift({
        value: '',
        text: 'Select a creative'
      })

      return options
    }
  },

  watch: {
    showCreativeId: {
      async handler(newVal) {
        if (newVal) {
          await this.loadCreatives()
        }
      },
      immediate: true
    }
  },

  async created() {
    await this.checkRTTEnabled()
  },

  methods: {
    ...mapActions('ringTreeTargetRequest', ['createRequest']),
    ...mapActions('creative', ['getCreatives']),
    ...mapActions('sellerOffer', ['checkCanCreateRingTreeTarget']),

    async checkRTTEnabled() {
      this.isCheckingRTTEnabled = true
      try {
        await this.checkCanCreateRingTreeTarget()
      } finally {
        this.isCheckingRTTEnabled = false
      }
    },

    async loadCreatives() {
      try {
        this.loadingCreatives = true
        await this.getCreatives()
      } catch (error) {
        showErrorMessage('Unable to load creatives.')
      } finally {
        this.loadingCreatives = false
      }
    },

    async onSubmit() {
      try {
        this.isSubmitting = true

        const requestData = {
          trafficSourceSubId: this.rttRequestFormData.trafficSource,
          thirdParty: this.rttRequestFormData.thirdParty === 'true' || this.rttRequestFormData.thirdParty === true,
          creativeId: this.rttRequestFormData.creativeId || '',
          createRingTree: this.rttRequestFormData.createRingTree
        }

        // Get registration ID from the prop
        const registrationId = this.sellerOfferRegistration?.id

        await this.createRequest({
          registrationId,
          requestData
        })

        showSuccessMessage('You\'ll be informed when Ring Tree Target has been created.')
        
        this.$router.push({ name: 'SellerAvailableOffers' })

      } catch (error) {
        showErrorMessage(error.response?.data?.title || 'Failed to create RTT request')
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>